import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Music2, Youtube, Facebook, Mail } from "lucide-react";
import { toast } from "sonner";
import { CONFIG } from "../../lib/config";
import { BRAND_LOGO } from "../../lib/assets";

const BREVO_FORM_URL = process.env.REACT_APP_BREVO_FORM_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!BREVO_FORM_URL) {
      toast.error("Signup temporarily unavailable. Please try again shortly.");
      return;
    }
    setLoading(true);
    try {
      const body = new FormData();
      body.append("EMAIL", trimmed);
      body.append("OPT_IN", "1");
      body.append("email_address_check", "");
      body.append("locale", "en");
      await fetch(BREVO_FORM_URL, { method: "POST", mode: "no-cors", body });
      toast.success(
        "Nearly there — check your inbox to confirm your place on the Watch List. 👀"
      );
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    }
    setLoading(false);
  };

  return (
    <footer
      data-testid="site-footer"
      className="navy-bg text-white pt-8 md:pt-20 pb-8"
      style={{ backgroundColor: "var(--pw-navy)" }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8 pb-10 md:pb-14">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-5">
              <img
                src={BRAND_LOGO}
                alt="People Watching"
                className="h-24 w-auto rounded-full"
                data-testid="footer-brand-logo"
                draggable="false"
              />
            </div>
            <p className="text-white/70 max-w-md leading-relaxed">
              {CONFIG.brand.supportingLine} A card game designed for the real world —
              made for airports, pubs, cafés, holidays and everywhere people gather.
            </p>

            <form
              onSubmit={submit}
              data-testid="footer-signup-form"
              className="mt-8 flex gap-2 max-w-md"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-email-input"
                aria-label="Email address"
                className="flex-1 px-5 py-3 rounded-full bg-white/10 border border-white/15 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[color:var(--pw-yellow)]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-signup-submit"
                className="pw-btn-primary !py-3 !px-6"
              >
                <Mail className="w-4 h-4" />
                Join
              </button>
            </form>
            <p
              className="mt-2 text-xs text-white/50 max-w-md"
              data-testid="footer-consent-note"
            >
              By joining you&rsquo;ll receive email updates from People Watching. You&rsquo;ll be
              asked to confirm your email, and you can unsubscribe any time.
            </p>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow mb-4" style={{ color: "var(--pw-yellow)" }}>Explore</p>
            <ul className="space-y-3 text-white/80">
              <li><button onClick={() => scrollTo("#how-it-works")} className="hover:text-[color:var(--pw-yellow)]" data-testid="footer-link-how-it-works">How It Works</button></li>
              <li><button onClick={() => scrollTo("#whats-in-the-box")} className="hover:text-[color:var(--pw-yellow)]" data-testid="footer-link-the-game">The Game</button></li>
              <li><button onClick={() => scrollTo("#faq")} className="hover:text-[color:var(--pw-yellow)]" data-testid="footer-link-faq">FAQ</button></li>
              <li><a className="hover:text-[color:var(--pw-yellow)]" href={`mailto:${CONFIG.contact.email}`} data-testid="footer-link-contact">Contact</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="eyebrow mb-4" style={{ color: "var(--pw-yellow)" }}>Support</p>
            <ul className="space-y-3 text-white/80">
              <li><Link className="hover:text-[color:var(--pw-yellow)]" to="/privacy" data-testid="footer-link-privacy">Privacy</Link></li>
              <li><Link className="hover:text-[color:var(--pw-yellow)]" to="/terms" data-testid="footer-link-terms">Terms</Link></li>
              <li><Link className="hover:text-[color:var(--pw-yellow)]" to="/shipping" data-testid="footer-link-shipping">Shipping</Link></li>
              <li><Link className="hover:text-[color:var(--pw-yellow)]" to="/returns" data-testid="footer-link-returns">Returns</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow mb-4" style={{ color: "var(--pw-yellow)" }}>Follow</p>
            <div className="flex flex-wrap gap-3 mb-6">
              <a
                href={CONFIG.social.instagram}
                target="_blank" rel="noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="w-11 h-11 rounded-full flex items-center justify-center border border-white/20 hover:bg-[color:var(--pw-yellow)] hover:text-[color:var(--pw-navy)] hover:border-transparent transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={CONFIG.social.tiktok}
                target="_blank" rel="noreferrer"
                data-testid="footer-tiktok"
                aria-label="TikTok"
                className="w-11 h-11 rounded-full flex items-center justify-center border border-white/20 hover:bg-[color:var(--pw-yellow)] hover:text-[color:var(--pw-navy)] hover:border-transparent transition-colors"
              >
                <Music2 className="w-5 h-5" />
              </a>
              <a
                href={CONFIG.social.youtube}
                target="_blank" rel="noreferrer"
                data-testid="footer-youtube"
                aria-label="YouTube"
                className="w-11 h-11 rounded-full flex items-center justify-center border border-white/20 hover:bg-[color:var(--pw-yellow)] hover:text-[color:var(--pw-navy)] hover:border-transparent transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href={CONFIG.social.facebook}
                target="_blank" rel="noreferrer"
                data-testid="footer-facebook"
                aria-label="Facebook"
                className="w-11 h-11 rounded-full flex items-center justify-center border border-white/20 hover:bg-[color:var(--pw-yellow)] hover:text-[color:var(--pw-navy)] hover:border-transparent transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              {CONFIG.brand.handle}
            </p>
          </div>
        </div>

        <div className="relative">
          <p
            className="font-display font-black leading-none tracking-tighter select-none pointer-events-none"
            style={{
              fontSize: "clamp(3rem, 18vw, 20rem)",
              color: "transparent",
              WebkitTextStroke: "1px rgba(255,255,255,0.15)",
              letterSpacing: "-0.05em",
            }}
          >
            PEOPLE WATCHING
          </p>
        </div>

        <div className="pt-8 mt-4 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-white/60 text-sm">
          <p>© {new Date().getFullYear()} People Watching. {CONFIG.brand.domain}</p>
          <p>{CONFIG.brand.supportingLine}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
